import React, { useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai'; // Import useAtomValue
import { useForm, FormProvider } from 'react-hook-form';
import { useSignIn, useSignUp } from '@clerk/nextjs';
import { authIdentifierTypeAtom, authModeAtom, authStepAtom, authRedirectToAtom } from 'auth/atoms'; // Import redirectToAtom
import VerificationInput from 'components/auth/components/VerificationInput';
import Button from 'components/ui/Button/Button';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import useTrackComponentMount from 'hooks/useTrackComponentMount';
import useRudderStackTrack from 'hooks/useRudderStackTrack';

type VerificationValues = {
  code: string | null;
};

const VerificationStep = () => {
  const { enqueueSnackbar } = useLazySnackbar();
  const [identifierType] = useAtom(authIdentifierTypeAtom);
  const [mode] = useAtom(authModeAtom);
  const [, setStep] = useAtom(authStepAtom);
  const methods = useForm<VerificationValues>({ defaultValues: { code: '' } });
  const { handleSubmit } = methods;
  const { signIn, setActive, isLoaded } = useSignIn();
  const { signUp } = useSignUp();
  const redirectTo = useAtomValue(authRedirectToAtom); // Use useAtomValue to read the atom

  const cooldownTime = 15;
  const [cooldown, setCooldown] = useState(0);

  useTrackComponentMount('show_form', {
    component_type: 'form',
    object: `verify_your_${identifierType}`,
  });
  const rudderTrack = useRudderStackTrack();

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const onSubmit = async ({ code }: VerificationValues) => {
    if (!isLoaded) return;
    try {
      if (mode === 'signin') {
        // For phone sign‑in verification:
        const strategy = identifierType === 'phone' ? 'phone_code' : 'email_code';
        const signInAttempt = await signIn.attemptFirstFactor({ strategy, code });
        if (signInAttempt.status === 'complete') {
          rudderTrack('login_success', {
            object: `verify_your_${identifierType}`,
          });
          await setActive({ session: signInAttempt.createdSessionId, redirectUrl: redirectTo }); // Use redirectTo from atom
          setStep('identifier');
        }
      } else {
        // For sign‑up verification, use the signUp attempt.
        // eslint-disable-next-line no-lonely-if
        if (identifierType === 'phone') {
          const verificationAttempt = await signUp.attemptPhoneNumberVerification({ code });
          if (verificationAttempt.status === 'complete') {
            rudderTrack('signup_success', {
              object: 'verify_your_phone',
            });
            await setActive({ session: verificationAttempt.createdSessionId, redirectUrl: redirectTo }); // Use redirectTo from atom
            setStep('identifier');
          }
        } else {
          const verificationAttempt = await signUp.attemptEmailAddressVerification({ code });
          if (verificationAttempt.status === 'complete') {
            rudderTrack('signup_success', {
              object: 'verify_your_email',
            });
            await setActive({ session: verificationAttempt.createdSessionId, redirectUrl: redirectTo }); // Use redirectTo from atom
            setStep('identifier');
          }
        }
      }
    } catch (err: any) {
      enqueueSnackbar(err.message, {
        variant: 'error',
        autoHideDuration: 5000,
      });
      console.error('Verification error:', err);
    }
  };

  const handleResendVerificationCode = async () => {
    if (!isLoaded || cooldown > 0) return;

    setCooldown(cooldownTime);

    try {
      if (identifierType === 'phone') {
        await signUp.preparePhoneNumberVerification({ strategy: 'phone_code' });
      } else {
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      }
    } catch (err) {
      enqueueSnackbar(err.message, {
        variant: 'error',
        autoHideDuration: 5000,
      });
      console.error('Resend Verfication Error:', err);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <VerificationInput
          headerText={`Verify your ${identifierType}`}
          resendCode={handleResendVerificationCode}
          resendCodeDisabled={cooldown > 0}
          resendCooldown={cooldown}
          onBackClick={() => {
            setStep('signupDetails');
          }}
        />
        <Button type="submit" text="Verify" fullWidth />
      </form>
    </FormProvider>
  );
};

export default VerificationStep;
